/**
 * Deferred, accessible video for the Timecard continuous body.
 *
 * Used by both the opening slot and any inline video insertion, so the two cannot drift into
 * different autoplay, pause or reduced-motion behaviour on the same page.
 *
 * Why a custom element rather than an inline script: the theme editor injects and removes section
 * markup at runtime, and a custom element gets `connectedCallback` / `disconnectedCallback` for
 * free. An inline script would bind once at first render, leave observers attached to removed
 * nodes, and double-bind every time the merchant edited the section.
 *
 * Deferral is real rather than declarative. `preload="none"` is only a hint and browsers ignore it
 * for a video carrying `autoplay` and a `src` — the file starts downloading while it is still far
 * offscreen. So the element ships with no sources at all: they are attached the first time the
 * video comes near the viewport, which is the only point at which the bytes are worth spending.
 */

const NEAR_VIEWPORT = '200px';

class TimecardVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-timecard-video-toggle]');
    if (!this.video) return;

    this.wantsAutoplay = this.dataset.autoplay === 'true';

    // Derived from the DOM, not reset to false. The theme editor moves a section by detaching and
    // reattaching the same nodes, and the `<source>` elements survive that move — so assuming
    // "not attached" on every connect appends a second copy of every source each time.
    this.sourcesAttached = this.video.querySelector('source') !== null;

    this.inView = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    /*
      Responsive sources, when the caller supplied a pair.

      Two independent files rather than one scaled both ways: the mobile cut is 4:5 and the desktop
      cut is 16:7, so serving either to the other viewport would crop the frame. Only the matching
      one is ever attached, so a phone never downloads the desktop file.

      Absent these attributes the element behaves exactly as before, which is what leaves the
      inline insertion and the Halloween bar untouched.
    */
    this.variants = {
      mobile: this.parseSources(this.dataset.sourcesMobile),
      desktop: this.parseSources(this.dataset.sourcesDesktop)
    };
    this.isResponsive = this.variants.mobile.length > 0 && this.variants.desktop.length > 0;
    this.breakpointQuery = this.isResponsive
      ? window.matchMedia(`(min-width: ${Number(this.dataset.breakpoint) || 750}px)`)
      : null;
    this.activeVariant = null;

    // Defaults to 1, so any caller that does not ask for a rate keeps normal speed.
    this.playbackRate = Number(this.dataset.playbackRate) || 1;

    this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
    this.onToggleClick = this.onToggleClick.bind(this);
    this.syncToggle = this.syncToggle.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.applyPlaybackRate = this.applyPlaybackRate.bind(this);
    this.onSourceError = this.onSourceError.bind(this);

    this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
    this.breakpointQuery?.addEventListener('change', this.onBreakpointChange);
    // Re-asserted after every load, because `load()` resets playbackRate to defaultPlaybackRate
    // and a reload or a source swap would otherwise silently drop back to 1.
    this.video.addEventListener('loadedmetadata', this.applyPlaybackRate);
    this.applyPlaybackRate();
    this.video.addEventListener('play', this.syncToggle);
    this.video.addEventListener('pause', this.syncToggle);
    this.video.addEventListener('error', this.onSourceError);
    if (this.toggle) this.toggle.addEventListener('click', this.onToggleClick);

    this.observe();
    this.syncToggle();
  }

  disconnectedCallback() {
    // Everything bound above is released here. Without this the theme editor accumulates one live
    // observer per edit, all pointing at detached nodes.
    this.observer?.disconnect();
    this.reducedMotion?.removeEventListener('change', this.onReducedMotionChange);
    this.breakpointQuery?.removeEventListener('change', this.onBreakpointChange);
    this.video?.removeEventListener('loadedmetadata', this.applyPlaybackRate);
    this.video?.removeEventListener('error', this.onSourceError);
    this.video?.removeEventListener('play', this.syncToggle);
    this.video?.removeEventListener('pause', this.syncToggle);
    this.toggle?.removeEventListener('click', this.onToggleClick);
    this.video?.pause();
  }

  observe() {
    if (!('IntersectionObserver' in window)) {
      // No observer available: attach immediately so the video still works, and accept the cost.
      this.inView = true;
      this.attachSources();
      this.maybeAutoplay();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.inView = entry.isIntersecting;
          if (entry.isIntersecting) {
            this.attachSources();
            this.maybeAutoplay();
          } else if (this.wantsAutoplay && !this.video.paused) {
            // Offscreen playback is decode work nobody is watching.
            this.video.pause();
          }
        }
      },
      { rootMargin: NEAR_VIEWPORT }
    );
    this.observer.observe(this);
  }

  /** Parses a sources attribute, tolerating an absent or malformed value. */
  parseSources(raw) {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.url) : [];
    } catch {
      return [];
    }
  }

  /** Which variant the current viewport wants, or null when this element is not responsive. */
  wantedVariant() {
    if (!this.isResponsive) return null;
    return this.breakpointQuery.matches ? 'desktop' : 'mobile';
  }

  /** Moves the declared sources onto the video. Idempotent per variant. */
  attachSources() {
    const wanted = this.wantedVariant();

    if (wanted) {
      // Already showing the right file; re-attaching would restart it for no reason.
      if (this.activeVariant === wanted) return;
      this.swapTo(wanted);
      return;
    }

    if (this.sourcesAttached) return;
    this.sourcesAttached = true;

    const sources = this.parseSources(this.dataset.sources);
    if (!sources.length) return;

    this.writeSources(sources);
  }

  /** Replaces the attached file with the other variant, preserving play state. */
  swapTo(variant) {
    const sources = this.variants[variant];
    if (!sources.length) return;

    const wasPlaying = !this.video.paused;

    for (const node of [...this.video.querySelectorAll('source')]) node.remove();
    delete this.dataset.failed;
    this.activeVariant = variant;
    this.sourcesAttached = true;
    this.writeSources(sources);

    // A swap mid-playback should not read as a stop. The rate is reapplied by the loadedmetadata
    // listener, which fires again for the new file.
    if (wasPlaying && !this.userPaused) this.maybeAutoplay();
  }

  writeSources(sources) {
    for (const item of sources) {
      const source = document.createElement('source');
      source.src = item.url;
      if (item.mime_type) source.type = item.mime_type;
      this.video.appendChild(source);
    }
    this.video.load();
    this.applyPlaybackRate();
  }

  onBreakpointChange() {
    // Only meaningful once something is attached; before that the observer picks the right variant
    // when the element first comes into view.
    if (!this.activeVariant) return;
    const wanted = this.wantedVariant();
    if (wanted && wanted !== this.activeVariant) this.swapTo(wanted);
  }

  /**
   * Both the default and the current rate.
   *
   * `defaultPlaybackRate` is what a later `load()` restores to, so setting only `playbackRate`
   * would hold until the first reload or source swap and then quietly revert to 1.
   */
  applyPlaybackRate() {
    if (!this.video) return;
    if (this.video.defaultPlaybackRate !== this.playbackRate) {
      this.video.defaultPlaybackRate = this.playbackRate;
    }
    if (this.video.playbackRate !== this.playbackRate) {
      this.video.playbackRate = this.playbackRate;
    }
  }

  maybeAutoplay() {
    if (!this.wantsAutoplay) return;
    if (this.dataset.failed === 'true') return;
    if (this.reducedMotion.matches) return;
    if (this.userPaused) return;
    if (!this.video.paused) return;

    // Viewport state is checked here rather than only at the observer callback, because this is
    // also reached when the reduced-motion preference is switched off. Without the check that
    // change would start playback on a video the visitor has long since scrolled past.
    if (!this.inView) return;

    // play() returns a promise that rejects on autoplay policy, a decode failure, or the element
    // being removed mid-play. Unhandled it surfaces as a console error on an otherwise fine page,
    // so the rejection resolves to "leave it paused and let the control say Play".
    const attempt = this.video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        this.pausedByScroll = false;
        this.syncToggle();
      });
    }
  }

  /**
   * A source that will not load.
   *
   * The poster frame stays on screen by itself, so the slot still shows a real product rather than
   * a black rectangle. What needs handling is the state around it: playback is marked failed so a
   * later autoplay attempt does not retry a source known to be broken, and the control is synced so
   * it cannot sit there claiming to be playing something that never started.
   */
  onSourceError() {
    this.dataset.failed = 'true';
    this.video.pause();
    this.syncToggle();
  }

  onReducedMotionChange() {
    if (this.reducedMotion.matches) {
      this.video.pause();
    } else {
      this.maybeAutoplay();
    }
    this.syncToggle();
  }

  onToggleClick() {
    if (this.video.paused) {
      this.userPaused = false;
      this.attachSources();
      const attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => this.syncToggle());
    } else {
      // An explicit pause outranks scrolling back into view.
      this.userPaused = true;
      this.video.pause();
    }
    this.syncToggle();
  }

  syncToggle() {
    if (!this.toggle || !this.video) return;
    const paused = this.video.paused;
    const label = paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
    this.toggle.textContent = label;
    this.toggle.setAttribute('aria-label', label);
    this.toggle.setAttribute('aria-pressed', paused ? 'false' : 'true');
  }
}

if (!customElements.get('timecard-video')) {
  customElements.define('timecard-video', TimecardVideo);
}
